import { Badge, Button, DataTable, dialogs, EmptyState, PageHeader, Pagination, Select, toast } from "@cafe/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, dt, errorText, http, INVOICE_STATUS, money, useCan } from "../lib/api";

type Inv = { id: string; number: string; companyId: string; companyName: string | null; status: string; kind: string; total: number; currency: string; issuedAt: string; dueDate: string };

/** Все счета платформы: фильтр по статусу, ручное подтверждение банковского перевода (US-PA-04). */
export default function InvoicesPage() {
  const can = useCan();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [status, setStatus] = useState("open");
  const [page, setPage] = useState(1);
  const list = useQuery({ queryKey: ["invoices", status, page], queryFn: () => api<{ items: Inv[]; meta: { total: number } }>("GET", "/invoices", undefined, { withMeta: true, query: { status: status === "all" ? undefined : status, page, pageSize: 50 } }) });
  const markPaid = (i: Inv) =>
    dialogs.confirm({
      title: `Оплата ${i.number}`, description: `${i.companyName ?? ""}: ${money(i.total, i.currency)}`, confirmText: "Деньги получены",
      onConfirm: async () => {
        try {
          await http.post(`/invoices/${i.id}/mark-paid`, { method: "bank_transfer", reference: `${i.number}-${Date.now()}` });
          toast.success("Оплата подтверждена");
          void qc.invalidateQueries({ queryKey: ["invoices"] });
        } catch (e) {
          toast.error(errorText(e));
        }
      },
    });
  return (
    <>
      <PageHeader title="Счета" description="Счета клиентов за подписку и дополнения." />
      <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }} className="sm:w-56" options={[{ value: "all", label: "Все" }, ...Object.entries(INVOICE_STATUS).map(([k, v]) => ({ value: k, label: v.label }))]} />
      <DataTable rows={list.data?.items} loading={list.isPending} rowKey={(i) => i.id} onRowClick={(i) => navigate(`/companies/${i.companyId}`)} empty={<EmptyState icon={<FileText />} title="Счетов нет" />}
        columns={[
          { key: "n", header: "Номер", cell: (i) => <span className="font-medium tabular-nums">{i.number}</span>, mobile: "title" },
          { key: "c", header: "Компания", cell: (i) => i.companyName ?? "—", mobile: "subtitle" },
          { key: "d", header: "Оплатить до", cell: (i) => <span className={i.status === "open" && new Date(i.dueDate) < new Date() ? "text-danger" : ""}>{dt(i.dueDate)}</span> },
          { key: "t", header: "Сумма", align: "end", cell: (i) => <span className="tabular-nums">{money(i.total, i.currency)}</span> },
          { key: "s", header: "Статус", cell: (i) => <Badge tone={INVOICE_STATUS[i.status]?.tone ?? "neutral"} dot>{INVOICE_STATUS[i.status]?.label ?? i.status}</Badge> },
        ]}
        actions={(i) => can("billing.manage") && i.status === "open" && <Button size="sm" onClick={() => void markPaid(i)}>Отметить оплату</Button>} />
      {list.data && <Pagination page={page} pageSize={50} total={list.data.meta.total} onPageChange={setPage} />}
    </>
  );
}
